import { LitElement, html } from "lit";

import { customElement, property, state } from "lit/decorators.js";

import type { FileContext } from "api-contracts/rpc/getFileUploadUrl";
import { DragDropController } from "ui-common/drag-drop-controller";
import { FileUploadError, formatUploadError, uploadFile } from "ui-common/services/file-upload";
// Translation functions removed - using configurable text properties instead
import { getAssetUrl } from "helpers/assetHelpers";

import { styles } from "./file-cropper.css";

import "../base-image-cropper/base-image-cropper";
import "../button/wow-button";
import { NotificationService } from "../notification/notification-service";
import type { ViewportType } from "../base-image-cropper/base-image-cropper";

interface Transform {
  x: number;
  y: number;
  scale: number;
}

interface ViewportConfig {
  type: ViewportType;
  aspectRatio: number;
  outputWidth: number;
  outputHeight: number;
}

interface PointerState {
  isPointerDown: boolean;
  lastPointerX: number;
  lastPointerY: number;
  initialDistance?: number;
  initialScale?: number;
}

@customElement("file-cropper")
export class FileCropper extends LitElement {
  static override styles = styles;

  private internals: ElementInternals;
  static formAssociated = true;

  private dragDrop = new DragDropController(this, (file) => this.loadImage(file));

  @property({ type: String })
  viewport: ViewportType = "square";

  @property({ type: Number })
  vpWidth = 200;

  @property({ type: Number })
  vpHeight = 200;

  @property({ type: String })
  context: FileContext = "brand_asset";

  @property({ type: String })
  currentFileUrl?: string;

  @property({ type: String })
  uploadSubtitle = "Upload an image";

  @property({ type: String })
  optionalText = "(optional)";

  @property({ type: String })
  removeButtonText = "Remove";

  @property({ type: String })
  saveButtonText = "Save";

  @property({ type: String })
  uploadingText = "Uploading...";

  @property({ type: String })
  headerText = "Crop file";

  @state()
  private currentState: "initial" | "cropping" = "initial";

  @state()
  private selectedFile?: File;

  @state()
  private imageElement?: HTMLImageElement;

  @state()
  private uploading = false;

  private canvasRef: HTMLCanvasElement;
  private transform: Transform = { x: 0, y: 0, scale: 1 };
  private pointerState: PointerState = {
    isPointerDown: false,
    lastPointerX: 0,
    lastPointerY: 0,
  };
  private activePointers: PointerEvent[] = [];

  constructor() {
    super();
    this.internals = this.attachInternals();
    this.canvasRef = this.createCanvas();
  }

  private _value: string = "";

  // Form API: Get current value (file ID)
  get value(): string {
    return this._value;
  }

  // Form API: Set value programmatically
  set value(fileId: string) {
    this._value = fileId;
    this.internals.setFormValue(fileId);
    this.requestUpdate();
  }

  private get viewportConfig(): ViewportConfig {
    // For circle, ensure vpHeight equals vpWidth (diameter)
    const effectiveHeight = this.viewport === "circle" ? this.vpWidth : this.vpHeight;

    return {
      type: this.viewport,
      aspectRatio: this.vpWidth / effectiveHeight,
      outputWidth: this.vpWidth,
      outputHeight: effectiveHeight,
    };
  }

  private handleFileInput = (event: Event) => {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) {
      // User cancelled the file picker - dispatch cancel event
      this.dispatchEvent(
        new CustomEvent("cancel", {
          bubbles: true,
          composed: true,
        }),
      );
      return;
    }

    if (!file.type.startsWith("image/")) {
      this.dispatchEvent(
        new CustomEvent("error", {
          detail: { message: "Please select an image file" },
        }),
      );
      return;
    }

    this.selectedFile = file;
    this.loadImage(file);
    input.value = "";
  };

  loadImage = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        this.imageElement = img;
        this.currentState = "cropping";
        this.requestUpdate();
        // Dispatch crop-started event when cropper actually opens
        this.dispatchEvent(
          new CustomEvent("crop-started", {
            bubbles: true,
            composed: true,
          }),
        );
        // Wait for the next frame to ensure canvas is rendered
        requestAnimationFrame(() => {
          this.resetTransform();
        });
      };
      img.src = e.target?.result as string;
    };
    reader.readAsDataURL(file);
  };

  private resetTransform = () => {
    this.transform = {
      x: 0,
      y: 0,
      scale: 1,
    };

    this.drawCanvas();
  };

  private calculateCanvasDimensions() {
    // Get the modal body element (canvas parent)
    const modalBody = this.renderRoot.querySelector("[slot=one-column]");
    if (!modalBody) {
      // Fallback if modal not found
      return { width: this.vpWidth + 20, height: 2 * this.vpHeight };
    }

    // Get parent's available space (accounting for padding)
    const parentRect = modalBody.getBoundingClientRect();
    const computedStyle = getComputedStyle(modalBody);
    const paddingHorizontal = parseFloat(computedStyle.paddingLeft) + parseFloat(computedStyle.paddingRight);

    return {
      width: parentRect.width - paddingHorizontal,
      height: 2 * this.vpHeight,
    };
  }

  private isCanvasDrawScheduled = false;

  private drawCanvas = async () => {
    if (this.isCanvasDrawScheduled) {
      return;
    }

    this.isCanvasDrawScheduled = true;

    requestAnimationFrame(() => {
      const canvas = this.canvasRef;
      if (!canvas || !this.imageElement) return;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      // Use dynamic canvas dimensions
      const { width: canvasWidth, height: canvasHeight } = this.calculateCanvasDimensions();

      // Get device pixel ratio for sharp rendering
      const dpr = window.devicePixelRatio || 1;

      // Set canvas internal resolution
      canvas.width = canvasWidth * dpr;
      canvas.height = canvasHeight * dpr;

      // Scale the canvas back down using CSS
      canvas.style.width = canvasWidth + "px";
      canvas.style.height = canvasHeight + "px";

      // Scale the drawing context so everything draws at the higher resolution
      ctx.scale(dpr, dpr);

      // Disable image smoothing for crisp rendering
      ctx.imageSmoothingEnabled = false;

      // Clear canvas
      ctx.clearRect(0, 0, canvasWidth, canvasHeight);

      // Calculate viewport dimensions with 10px margins from canvas edges
      const config = this.viewportConfig;
      const maxWidth = canvasWidth - 20; // 10px on each side
      const maxHeight = canvasHeight - 20; // 10px on each side

      // Scale down proportionally if needed to fit within canvas boundaries
      const scaleX = maxWidth / config.outputWidth;
      const scaleY = maxHeight / config.outputHeight;
      const scale = Math.min(1, scaleX, scaleY);

      const viewportWidth = config.outputWidth * scale;
      const viewportHeight = config.outputHeight * scale;
      const viewportX = (canvasWidth - viewportWidth) / 2;
      const viewportY = (canvasHeight - viewportHeight) / 2;

      // Draw image with transform
      ctx.save();
      ctx.translate(canvasWidth / 2, canvasHeight / 2);
      ctx.translate(this.transform.x, this.transform.y);
      ctx.scale(this.transform.scale, this.transform.scale);

      const imgWidth = this.imageElement.naturalWidth;
      const imgHeight = this.imageElement.naturalHeight;
      ctx.drawImage(this.imageElement, -imgWidth / 2, -imgHeight / 2, imgWidth, imgHeight);
      ctx.restore();

      // Draw viewport overlay (excluding viewport area)
      ctx.save();
      ctx.fillStyle = "rgba(0, 0, 0, 0.5)";

      // Create clipping path that excludes the viewport
      ctx.beginPath();
      if (config.type === "circle") {
        // Create path for entire canvas
        ctx.rect(0, 0, canvasWidth, canvasHeight);
        // Subtract viewport circle (counter-clockwise for hole)
        ctx.arc(canvasWidth / 2, canvasHeight / 2, viewportWidth / 2, 0, 2 * Math.PI, true);
      } else {
        // Create path for entire canvas
        ctx.rect(0, 0, canvasWidth, canvasHeight);
        // Subtract viewport rectangle
        ctx.rect(viewportX, viewportY, viewportWidth, viewportHeight);
      }
      ctx.clip("evenodd"); // Use even-odd rule to create hole
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);
      ctx.restore();

      // Draw viewport border with pixel-perfect positioning
      ctx.save();
      ctx.strokeStyle = "#ffffff";
      ctx.lineWidth = 1;

      if (config.type === "circle") {
        ctx.beginPath();
        // For crisp circles, center coordinates need to be at pixel boundaries
        const centerX = Math.round(canvasWidth / 2) + 0.5;
        const centerY = Math.round(canvasHeight / 2) + 0.5;
        const radius = Math.round(viewportWidth / 2);
        ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
        ctx.stroke();
      } else {
        // For crisp rectangles, align coordinates to pixel boundaries
        const x = Math.round(viewportX) + 0.5;
        const y = Math.round(viewportY) + 0.5;
        const width = Math.round(viewportWidth);
        const height = Math.round(viewportHeight);
        ctx.strokeRect(x, y, width, height);
      }
      ctx.restore();
      this.isCanvasDrawScheduled = false;
    });
  };

  private handlePointerDown = (event: PointerEvent) => {
    event.preventDefault();
    this.activePointers.push(event);

    if (this.activePointers.length === 1) {
      this.pointerState.isPointerDown = true;
      this.pointerState.lastPointerX = event.clientX;
      this.pointerState.lastPointerY = event.clientY;
    } else if (this.activePointers.length === 2) {
      // Start pinch gesture
      const [p1, p2] = this.activePointers;
      if (p1 && p2) {
        this.pointerState.initialDistance = Math.hypot(p2.clientX - p1.clientX, p2.clientY - p1.clientY);
        this.pointerState.initialScale = this.transform.scale;
      }
    }
  };

  private handlePointerMove = (event: PointerEvent) => {
    event.preventDefault();

    // Update pointer in array
    const index = this.activePointers.findIndex((p) => p.pointerId === event.pointerId);
    if (index !== -1) {
      this.activePointers[index] = event;
    }

    if (this.activePointers.length === 1 && this.pointerState.isPointerDown) {
      // Pan gesture
      const deltaX = event.clientX - this.pointerState.lastPointerX;
      const deltaY = event.clientY - this.pointerState.lastPointerY;

      this.transform.x += deltaX;
      this.transform.y += deltaY;

      this.pointerState.lastPointerX = event.clientX;
      this.pointerState.lastPointerY = event.clientY;

      this.drawCanvas();
    } else if (this.activePointers.length === 2) {
      // Pinch gesture
      const [p1, p2] = this.activePointers;
      if (p1 && p2) {
        const currentDistance = Math.hypot(p2.clientX - p1.clientX, p2.clientY - p1.clientY);

        if (this.pointerState.initialDistance && this.pointerState.initialScale) {
          const scaleChange = currentDistance / this.pointerState.initialDistance;
          const newScale = this.pointerState.initialScale * scaleChange;
          this.transform.scale = Math.max(0.1, Math.min(5, newScale));
          this.drawCanvas();
        }
      }
    }
  };

  private handlePointerUp = (event: PointerEvent) => {
    this.activePointers = this.activePointers.filter((p) => p.pointerId !== event.pointerId);

    if (this.activePointers.length === 0) {
      this.pointerState.isPointerDown = false;
    } else if (this.activePointers.length === 1) {
      // Reset single pointer state
      const remaining = this.activePointers[0];
      if (remaining) {
        this.pointerState.lastPointerX = remaining.clientX;
        this.pointerState.lastPointerY = remaining.clientY;
        this.pointerState.initialDistance = undefined;
        this.pointerState.initialScale = undefined;
      }
    }
  };

  private handleWheel = (event: WheelEvent) => {
    event.preventDefault();
    const scaleChange = event.deltaY > 0 ? 0.9 : 1.1;
    this.transform.scale = Math.max(0.1, Math.min(5, this.transform.scale * scaleChange));
    this.drawCanvas();
  };

  private handleCancel = () => {
    this.currentState = "initial";
    this.selectedFile = undefined;
    this.imageElement = undefined;
    this.transform = { x: 0, y: 0, scale: 1 };

    // Dispatch cancel event to notify parent components
    this.dispatchEvent(
      new CustomEvent("cancel", {
        bubbles: true,
        composed: true,
      }),
    );
  };

  private handleRemove = () => {
    this.currentFileUrl = undefined;
    this._value = "";
    this.internals.setFormValue("");

    this.internals.setValidity({});

    this.dispatchEvent(
      new CustomEvent("change", {
        bubbles: true,
        detail: { fileId: "", imageUrl: "" },
      }),
    );
  };

  public setCustomValidity(error?: string) {
    if (error) {
      this.internals.setValidity({ customError: true }, error);
    } else {
      this.internals.setValidity({});
    }
    this.requestUpdate();
  }

  // Handles save event from base-image-cropper
  private handleBaseCropperSave = async (event: CustomEvent) => {
    if (this.uploading) return;
    try {
      this.uploading = true;
      const croppedFile = event.detail?.file;
      if (!croppedFile) {
        throw new Error("Failed to generate cropped file");
      }
      const result = await uploadFile(croppedFile, this.context);
      this.currentFileUrl = result.url;
      this.currentState = "initial";
      this.internals.setFormValue(result.fileId);
      this.internals.setValidity({});
      this.dispatchEvent(
        new CustomEvent("change", {
          bubbles: true,
          detail: { fileId: result.fileId, imageUrl: result.url },
        }),
      );
      NotificationService.success("Image uploaded successfully!");
    } catch (error) {
      if (error instanceof FileUploadError) {
        NotificationService.error(formatUploadError(error));
      } else {
        NotificationService.error("Failed to upload image. Please try again.");
      }
      this.internals.setValidity({ customError: true }, "Failed to upload image. Please try again.");
    } finally {
      this.uploading = false;
    }
  };

  private generateCroppedBlob = async (): Promise<Blob | null> => {
    if (!this.imageElement) return null;

    const config = this.viewportConfig;

    // Calculate viewport dimensions using the same logic as drawCanvas
    const { width: viewCanvasWidth, height: viewCanvasHeight } = this.calculateCanvasDimensions();
    const maxWidth = viewCanvasWidth - 20; // 10px on each side
    const maxHeight = viewCanvasHeight - 20; // 10px on each side
    const scaleViewport = Math.min(1, maxWidth / config.outputWidth, maxHeight / config.outputHeight);

    // Calculate the source region in the original image coordinates
    // This maps from the viewport back to the original image
    const sourceScale = 1 / (scaleViewport * this.transform.scale);
    const sourceWidth = config.outputWidth * sourceScale;
    const sourceHeight = config.outputHeight * sourceScale;

    // Calculate source position (accounting for pan and center offset)
    const sourceX = this.imageElement.naturalWidth / 2 - sourceWidth / 2 - this.transform.x * sourceScale;
    const sourceY = this.imageElement.naturalHeight / 2 - sourceHeight / 2 - this.transform.y * sourceScale;

    // Create a high-quality intermediate canvas for processing
    const qualityCanvas = document.createElement("canvas");
    const qualityCtx = qualityCanvas.getContext("2d");
    if (!qualityCtx) return null;

    // Set intermediate canvas size (larger for better quality)
    const scaleFactor = 2; // Supersampling factor
    qualityCanvas.width = config.outputWidth * scaleFactor;
    qualityCanvas.height = config.outputHeight * scaleFactor;

    // Enable high-quality image smoothing
    qualityCtx.imageSmoothingEnabled = true;
    qualityCtx.imageSmoothingQuality = "high";

    // Apply clipping for circle viewport
    if (config.type === "circle") {
      qualityCtx.beginPath();
      qualityCtx.arc(qualityCanvas.width / 2, qualityCanvas.height / 2, qualityCanvas.width / 2, 0, 2 * Math.PI);
      qualityCtx.clip();
    }

    // Draw the cropped region from the original image directly
    // This avoids the quality loss from multiple transformations
    qualityCtx.drawImage(
      this.imageElement,
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight, // Source rectangle
      0,
      0,
      qualityCanvas.width,
      qualityCanvas.height, // Destination rectangle
    );

    // Create final output canvas
    const finalCanvas = document.createElement("canvas");
    const finalCtx = finalCanvas.getContext("2d");
    if (!finalCtx) return null;

    finalCanvas.width = config.outputWidth;
    finalCanvas.height = config.outputHeight;

    // Enable high-quality downscaling
    finalCtx.imageSmoothingEnabled = true;
    finalCtx.imageSmoothingQuality = "high";

    // Downscale from high-quality intermediate canvas to final size
    finalCtx.drawImage(qualityCanvas, 0, 0, finalCanvas.width, finalCanvas.height);

    return new Promise((resolve) => {
      finalCanvas.toBlob(resolve, "image/webp", 1);
    });
  };

  public async getCroppedBlob(): Promise<Blob | null> {
    return this.generateCroppedBlob();
  }

  public async getCroppedArrayBuffer(): Promise<ArrayBuffer | null> {
    const blob = await this.getCroppedBlob();
    return blob?.arrayBuffer() ?? null;
  }

  public async getCroppedFile(): Promise<File | null> {
    const blob = await this.getCroppedBlob();
    if (!blob || !this.selectedFile) return null;

    const baseName = this.selectedFile.name.split(".")[0] || "cropped";
    const fileName = `${baseName}-cropped.webp`;

    return new File([blob], fileName, { type: "image/webp" });
  }

  protected createCanvas() {
    const canvasRef = document.createElement("canvas");

    canvasRef.classList.add("crop-canvas");

    canvasRef.addEventListener("pointerdown", this.handlePointerDown.bind(this));
    canvasRef.addEventListener("pointermove", this.handlePointerMove.bind(this));
    canvasRef.addEventListener("pointerup", this.handlePointerUp.bind(this));
    canvasRef.addEventListener("wheel", this.handleWheel.bind(this));

    const resizeObserver = new ResizeObserver(() => {
      if (this.currentState === "cropping") {
        this.drawCanvas();
      }
    });
    resizeObserver.observe(canvasRef);
    return canvasRef;
  }

  protected override updated(changedProperties: Map<string, unknown>) {
    if (changedProperties.has("viewport")) {
      // For circle viewport, ensure vpHeight equals vpWidth
      if (this.viewport === "circle") {
        this.vpHeight = this.vpWidth;
      }
      if (this.currentState === "cropping") {
        this.resetTransform();
      }
    }
    if (changedProperties.has("vpWidth") && this.viewport === "circle") {
      // Keep vpHeight in sync with vpWidth for circle viewport
      this.vpHeight = this.vpWidth;
      if (this.currentState === "cropping") {
        this.requestUpdate();
        requestAnimationFrame(() => this.drawCanvas());
      }
    }
    if ((changedProperties.has("vpWidth") || changedProperties.has("vpHeight")) && this.currentState === "cropping") {
      this.requestUpdate();
      requestAnimationFrame(() => this.drawCanvas());
    }
  }

  private renderFallbackPlaceholder() {
    return html`
      <div class="upload-placeholder">
        <ph-camera class="camera-icon" size="20" weight="regular" color="currentColor"></ph-camera>
        <span>${this.uploadSubtitle} ${this.hasAttribute("required") ? "" : this.optionalText}</span>
      </div>
    `;
  }

  private renderCurrentFile() {
    if (!this.currentFileUrl) {
      return this.renderFallbackPlaceholder();
    }

    const src = getAssetUrl(this.currentFileUrl);
    if (!src) {
      return this.renderFallbackPlaceholder();
    }

    return html`
      <div class="current-file">
        <div class="preview-image-container">
          <img src=${src} alt="Current image" class="preview-image" />
        </div>
        <div class="upload-overlay">
          <ph-camera class="upload-icon" size="24"></ph-camera>
          <span class="upload-text">Change image</span>
        </div>
      </div>
    `;
  }

  private renderInitial() {
    return html`
      <div class="preview-container">
        <div
          class="upload-container ${this.uploading ? "uploading" : ""} ${this.dragDrop.isDragging ? "drag-over" : ""}"
          @click=${this.handleUploadClick}
          @dragover=${this.dragDrop.handleDragOver}
          @dragleave=${this.dragDrop.handleDragLeave}
          @drop=${this.dragDrop.handleDrop}
        >
          ${this.uploading
            ? html`
                <div class="uploading-state">
                  <ph-spinner class="spinner" size="24"></ph-spinner>
                  <span class="upload-text">Uploading...</span>
                </div>
              `
            : this.renderCurrentFile()}
          <input type="file" accept="image/*" @change=${this.handleFileInput} style="display: none;" />
        </div>
        ${this.currentFileUrl
          ? html`
              <div class="preview-controls">
                <wow-button variant="neutral" size="medium" @click=${this.handleRemove}>
                  ${this.removeButtonText}
                </wow-button>
              </div>
            `
          : ""}
      </div>
      ${this.internals.validationMessage
        ? html`<span class="error-message" slot="error">${this.internals.validationMessage}</span>`
        : ""}
    `;
  }

  private renderCropping() {
    return html`
      <base-image-cropper
        .open=${true}
        .imageUrl=${this.imageElement?.src}
        .viewport=${this.viewport}
        .outputWidth=${this.vpWidth}
        .outputHeight=${this.vpHeight}
        .headerText=${this.headerText}
        .footerButtonText=${this.uploading ? this.uploadingText : this.saveButtonText}
        @cropper-save=${this.handleBaseCropperSave}
        @cropper-cancel=${this.handleCancel}
      ></base-image-cropper>
    `;
  }

  private handleUploadClick = () => {
    if (this.uploading) return;
    const input = this.shadowRoot?.querySelector('input[type="file"]') as HTMLInputElement;
    this.dispatchEvent(new CustomEvent("open", { bubbles: true }));
    input?.click();
  };

  protected override render() {
    switch (this.currentState) {
      case "initial":
        return this.renderInitial();
      case "cropping":
        return this.renderCropping();
    }
  }
}

export type FileCropperChangeEvent = CustomEvent<{ fileId: string; imageUrl: string }>;

declare global {
  interface HTMLElementTagNameMap {
    "file-cropper": FileCropper;
  }
}
